import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useBetslipStore } from '@/store/betslip';
import { Icon, TrashCanIcon } from '@/components/icons/svg-icons';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useSiteStore } from '@/store/site';
import { useTranslations } from 'next-intl';
import { useBetslipContext } from '../contexts/betslip-context';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { Button } from '@nf/ui';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { BetModeControl } from './components/bet-mode-control';
import { BookingButtonProvider } from '../betslip-booking/contexts/booking-button-context';
import { BookingButton } from '../betslip-booking/components/booking-button';
import { BookingFeatureTip } from '../betslip-booking/components/booking-feature-tip';
import { useAsideBetslip } from '../hook/use-aside-betslip';
import { betslipHeaderStyle } from './bet-slip-header.style';
import { useAlertBetslip } from '../hook/use-alert-betslip';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { AnalyseKey, useConfigsAnalyseAttributeWithToggle } from '@/features/next-analyse';

export type  BasicBetSlipHeaderType = 'BetslipHeader' | 'NoTicketHeader' | 'QuickBetAsideHeader' | 'QuickBetBottomHeader' | 'empty';

// 若為basic style bet slip 一定使用此header
export const BasicBetSlipHeaderGroup = () => {
	const { activeAsideBetSlip } = useAsideBetslip();

	const [
		isParlayMode,
		isQuickBet,
		currentMultiTickets,
	] = useBetslipStore(state => [
		state.isParlayMode,
		state.isQuickBet,
		state.currentMultiTickets,
	]);

	const type: BasicBetSlipHeaderType = useMemo(() => {
		if (activeAsideBetSlip) {
			if (isParlayMode) {
				return 'empty';
			}
			return isQuickBet || currentMultiTickets.length === 0
				? 'QuickBetAsideHeader'
				: 'empty';
		}
		
		if (isQuickBet) {
			return 'QuickBetBottomHeader';
		}

		return currentMultiTickets.length > 0
			? 'BetslipHeader'
			: 'NoTicketHeader';
	}, [activeAsideBetSlip, isParlayMode, isQuickBet, currentMultiTickets.length]);
	
	return (
		<>
			{
				{
					'BetslipHeader': <BetslipHeader />,
					'NoTicketHeader': <NoTicketHeader />,
					'QuickBetAsideHeader': <QuickBetAsideHeader />,
					'QuickBetBottomHeader': <QuickBetBottomHeader />,
					'empty': null
				}[type]
			}
		</>
	)
}

export const BetslipHeader = () => {
	const t = useTranslations('General');
	const { isIndependentMode } = useBetslipContext();
	const { showAlert } = useAlertBetslip(isIndependentMode);
	const [
		isExpand,
		betslipType,
		setIsBookingBet,
		toggleIsExpand,
		currentMultiTickets,
		betslipTab,
		isParlayMode,
	] = useBetslipStore(state => [
		state.isExpand,
		state.betslipType,
		state.setIsBookingBet,
		state.toggleIsExpand,
		state.currentMultiTickets,
		state.betslipTab,
		state.isParlayMode,
	]);
	const { getRef } = useConfigsAnalyseAttributeWithToggle([{
		key: 'betslip-toggle',
		activeKey: 'collapse-single-bet-slip',
		inactiveKey: 'expand-single-bet-slip',
		isActive: isExpand,
		analyseKey: AnalyseKey.Refined
	}]);

	const isParlayTab = betslipTab === 'parlay';

	useEffect(() => {
		if (betslipType !== 'single') {
			setIsBookingBet(false);
		}
	}, [betslipType]);

	const onHeaderClick = () => {
		toggleIsExpand();
	}

	const getTitle = () => {
		if (isIndependentMode && (currentMultiTickets.length > 1 || isParlayMode)) {
			return isParlayTab ? t('lbl_mixparlay') : t('lbl_SingleBet');
		}
		return t('Betsilp');
	}

	const hasTicket = currentMultiTickets.length > 0;

	return (
		<div
			ref={getRef('betslip-toggle')}
			className={clsx(
				'bet-slip__header',
				betslipHeaderStyle.header,
				hasTicket && betslipHeaderStyle.headerBorder,
			)}
			onClick={onHeaderClick}
		>
			<div className="bet-slip__header-tool">
				{isParlayMode?(
					<div>
						<Icon name="betslip-bet" className={betslipHeaderStyle.betIcon} />
					</div>
				):(
					<BetModeControl type={'betMode'} />
				)}
				<div className={clsx('bet-slip__title', betslipStyle.headerTitle)}>
					{getTitle()}
				</div>
				
				{showAlert? (
					<div className={betslipHeaderStyle.alertIcon}>
						<Icon name="ticket-exclamation" />
					</div>
				): (hasTicket
					? <div className="bet-slip__badge">{currentMultiTickets.length}</div>
					: null
				)}
				{hasTicket ? <Icon name="dropdown" /> : null}
			</div>
			<BookingButtonProvider>
				<BookingButton />
				<BookingFeatureTip />
			</BookingButtonProvider>
			{currentMultiTickets.length > 1? <BetslipHeaderInfo /> : null}
		</div>
	);
};

/**
 * Betslip Header Info
 * * [galaxy] multi 展開才顯示此元件 (balance 會套用顯示開關) aside一律不顯示 
 * * [mars] single 以外才顯示此元件 (balance 僅在 quick bet 顯示, 且不套用顯示開關) aside一律不顯示 
 */
const BetslipHeaderInfo = () => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const { balance } = useBetslipContext();
	const [isShowBalance] = useSiteStore(
		state => [state.isShowBalance]
	);
	const [
		removeAllTicket
	] = useBetslipStore(
		state => [state.removeAllTicket]
	);
	const removeAllTicketWithStopPropagation = (event: React.MouseEvent<HTMLElement>) => {
		removeAllTicket();
		event.stopPropagation();
	}
	return (
		<div className="bet-slip__header-info">
			{
				view === ViewEnum.galaxy && isShowBalance ? (
					<div className="bet-slip__header-funds">
						{t('lbl_AvailableFunds')}
						<br />
						<div className={betslipStyle.balance}>{balance}</div>
					</div>
				) : null
			}
			<Button variant="icon" onClick={removeAllTicketWithStopPropagation}>
				<TrashCanIcon />
			</Button>
		</div>
	);
}

const NoTicketHeader = () => {
	const t = useTranslations('General');
	return (
		<div
			className={clsx(
				'bet-slip__header',
				betslipHeaderStyle.header,
				betslipHeaderStyle.headerBorder,
			)}
		>
			<div className="bet-slip__header-tool">
				<BetModeControl type={'betMode'} />
				<div className={clsx('bet-slip__title', betslipStyle.headerTitle)}>
					{t('Betsilp')}
				</div>
			</div>
		</div>
	);
}

const QuickBetAsideHeader = () => {
	const t = useTranslations('General');
	const { quickBetStake, currencyName, currencySymbol } = useBetslipContext();
	const view = useAppearanceStore(state => state.view);
	const [
		isQuickBet,
	] = useBetslipStore(state => [
		state.isQuickBet,
	]);
	const getTitle = () => {
		return view === ViewEnum.mars ? t('lbl_OneClickBet') : t('lbl_QuickBet');
	}
	return (
		<div
			className={clsx(
				'bet-slip__header',
				betslipHeaderStyle.header,
			)}
		>
			<div className="bet-slip__header-tool">
				<BetModeControl type={'quickBet'} />
				<div className={clsx('bet-slip__title', betslipStyle.headerTitle)}>
					{getTitle()}
				</div>
				{
					isQuickBet && quickBetStake > 0
						? (
							<div 
								className={clsx('bet-slip__quick-bet-stake', betslipHeaderStyle.stake)} 
							>
								<span>
									{`${currencyName} ${currencySymbol}${addCommasWithFix(quickBetStake)}`}
								</span>
							</div>
						)
						: null
				}
			</div>
		</div>
	);
}

const QuickBetBottomHeader = () => {
	const t = useTranslations('General');
	const { quickBetStake, currencyName, currencySymbol } = useBetslipContext();
	const view = useAppearanceStore(state => state.view);
	const { balance } = useBetslipContext();
	const [
		setIsQuickBetSettingModalOpen
	] = useBetslipStore(state => [
		state.setIsQuickBetSettingModalOpen
	]);

	const getTitle = () => {
		return view === ViewEnum.mars ? t('lbl_OneClickBet') : t('lbl_QuickBet');
	}

	const openQuickBetSettingModal = () => {
		setIsQuickBetSettingModalOpen(true);

		if (view === ViewEnum.mars) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 10,
				action: 3
			}

			setKafkaNormal(kafkaData);
		}
	}

	return (
		<div
			className={clsx(
				'bet-slip__header',
				betslipHeaderStyle.header,
			)}
		>
			<div className="bet-slip__header-tool">
				<BetModeControl type={'betMode'} />
				<div className={clsx('bet-slip__title', betslipStyle.headerTitle)}>
					{getTitle()}
				</div>
				{
					quickBetStake > 0
						? (
							<div 
								className={clsx('bet-slip__quick-bet-stake', betslipHeaderStyle.stake)} 
								onClick={openQuickBetSettingModal}
							>
								<span>
									{`${currencyName} ${currencySymbol}${addCommasWithFix(quickBetStake)}`}
								</span>
								<Icon name={'gear-setting'} className={betslipHeaderStyle.gearIcon} />
							</div>
						)
						: null
				}
			</div>
			<div className="bet-slip__header-info">
				<div className={clsx('bet-slip__header-funds', betslipHeaderStyle.QuickBetHeaderFunds)}>
					{t('lbl_AvailableFunds')}
					<br />
					<div className={betslipStyle.balance}>{balance}</div>
				</div>
			</div>
		</div>
	);
}